import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useVitalsWidgetSubscription } from '~/api/graphql/subscriptions/vitals'
import FeatureToggle from '~/constants/featureToggle'
import { useSubscriptionRetry } from '~/hooks/useSubscriptionRetry'
import { getUnitsState } from '~/store/duck/settings'
import { updateVitals } from '~/store/duck/vitals'
import { getConstantMap, getFeatureToggle } from '~/store/reducers/constants'
import { transformVitalsData } from '~/utils/vitalsTransformers'

/**
 * Custom hook that fetches vitals data via GraphQL subscription and returns
 * normalized/transformed data ready for component consumption.
 *
 * This hook encapsulates:
 * - GraphQL subscription to vitals data
 * - Data transformation from GraphQL format to component format
 * - Unit conversions and vital label processing
 * - Loading and error state management
 */
export const useNormalizedVitalsSubscription = (patientId: string) => {
  // Fetch data via GraphQL subscription
  const {
    data: subscriptionData,
    loading,
    error,
    restart,
  } = useVitalsWidgetSubscription(patientId)

  const vitalsData = subscriptionData?.vitalsWidget

  // Get necessary context for transformation
  const unitsState = useSelector(getUnitsState)
  const constantMap = useSelector(getConstantMap)
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  // Transform data using utility functions
  const normalizedVitals = useMemo(() => {
    if (!vitalsData) return {}

    return transformVitalsData(vitalsData, {
      unitsState,
      constantMap,
      isPatientSharingEnabled,
    })
  }, [vitalsData, unitsState, constantMap, isPatientSharingEnabled])

  const dispatch = useDispatch()
  const lastVitalsSignatureRef = useRef<string | null>(null)

  // Dispatch all available vitals into vitals slice as lightweight entities
  useEffect(() => {
    const widget = vitalsData as any
    if (!patientId || !widget) return

    const vitalTypeKeys = [
      'weight',
      'temperature',
      'heartRate',
      'respirationRate',
      'capillaryRefillTime',
      'mucousMembraneColorId',
      'mucousMembraneMoistureId',
      'skinTurgorId',
      'bodyConditionScore',
      'fearAnxietyScore',
      'painScore',
      'dyspnea',
      'mentationId',
      'pulseCharacterId',
      'bloodPressure',
      'spo2',
      'etco2',
      'o2Level',
      'isoSevoLevel',
      'recumbencyId',
      'muscleMassScore',
    ] as const

    // Build a compact signature to avoid redundant dispatches
    const signatureParts: string[] = []
    vitalTypeKeys.forEach((key) => {
      const arr = widget?.[key] as Array<{ creationDate: string }> | undefined
      if (arr?.length) {
        const last = arr.reduce(
          (acc, it) =>
            new Date(it.creationDate).getTime() >
            new Date(acc.creationDate).getTime()
              ? it
              : acc,
          arr[0],
        )
        signatureParts.push(`${key}:${arr.length}:${last.creationDate}`)
      }
    })
    const currentSignature = signatureParts.join('|')
    if (
      !currentSignature ||
      lastVitalsSignatureRef.current === currentSignature
    )
      return

    const vitalsMap: Record<string, any> = {}
    vitalTypeKeys.forEach((key) => {
      const items = widget?.[key] as
        | Array<{ creationDate: string; notes?: string; value: any }>
        | undefined
      if (!items?.length) return

      items.forEach((item) => {
        const id = `${patientId}:${key}:${item.creationDate}`
        // Normalize value for constant entity types: store id if object
        const rawValue = item.value as any
        const normalizedValue =
          rawValue && typeof rawValue === 'object' && 'id' in rawValue
            ? rawValue.id
            : rawValue

        vitalsMap[id] = {
          id,
          patientId,
          recordedDate: item.creationDate,
          [key]: { value: normalizedValue, notes: item.notes },
        }
      })
    })

    if (Object.keys(vitalsMap).length > 0) {
      lastVitalsSignatureRef.current = currentSignature
      dispatch(updateVitals(vitalsMap))
    }
  }, [dispatch, patientId, vitalsData])

  // Handle subscription errors with retry logic
  useSubscriptionRetry({
    error,
    restart,
    subscriptionName: 'Vitals subscription',
  })

  return {
    vitals: normalizedVitals,
    loading,
    error,
  }
}
